import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Flex, Link } from "theme-ui";
import moment from "moment-timezone";

import { themevals } from "theme/themevals";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import { useToast } from "components/toast";
import { BackArrowIcon } from "components/svg";

import { getLeadDuplicates, mergeLeadDuplicates, dismissLeadDuplicates, LEAD_STATUS_LABELS, type DuplicateGroup, type DuplicateMember } from "api/Leads";
import { Pill } from "./List";

/**
 * Leads > Duplicates (docs/leads/LEADS_NEXT.md s.2), modelled on
 * /locations/duplicates. One card per group of records the identity check
 * pairs (same email, company domain, website or normalised name). Per
 * group: pick the survivor, Merge the rest into it, or "Not duplicates"
 * (remembered per pair, so the group shrinks instead of hiding).
 *
 * A CLIENT in a group is always the survivor: a lead is merged INTO the
 * client, never the other way, and the client's own data is untouched -
 * the lead becomes notes on the client (LEADS_NEXT.md s.1).
 */

const CONFIDENCE: Record<DuplicateGroup["confidence"], { label: string; bg: string; fg: string }> = {
  exact: { label: "Same email", bg: "#FBEAEA", fg: "#A60000" },
  strong: { label: "Same domain / website", bg: "#FAEEDA", fg: "#633806" },
  probable: { label: "Same name", bg: "#F1EFE8", fg: "#5f5e5a" },
};

const work = (m: DuplicateMember) => m.rfqCount + m.quoteCount + m.proposalCount + m.siteCount + m.noteCount;

const suggestSurvivor = (g: DuplicateGroup): DuplicateMember => {
  const client = g.members.find((m) => m.kind === "client");
  if (client) return client;
  return [...g.members].sort((a, b) => work(b) - work(a) || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0];
};

const LeadDuplicates: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();
  const [groups, setGroups] = useState<DuplicateGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [survivorPick, setSurvivorPick] = useState<Record<string, string>>({});
  const [merge, setMerge] = useState<{ group: DuplicateGroup; survivor: DuplicateMember; others: DuplicateMember[] } | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const focus = params.get("focus");

  const refetch = useCallback(async () => {
    setLoading(true);
    const res: any = await getLeadDuplicates();
    setLoading(false);
    if (res?.error) return setError(res.error);
    setGroups(res.groups ?? []);
  }, []);

  useEffect(() => {
    document.title = `${process.env.REACT_APP_NAME} | Duplicate leads`;
    refetch();
  }, [refetch]);

  // Focused group (from a pill) first.
  const ordered = useMemo(() => {
    if (!focus) return groups;
    return [...groups].sort((a, b) => (b.members.some((m) => m.id === focus) ? 1 : 0) - (a.members.some((m) => m.id === focus) ? 1 : 0));
  }, [groups, focus]);

  const survivorOf = (g: DuplicateGroup): DuplicateMember => {
    const client = g.members.find((m) => m.kind === "client");
    if (client) return client; // forced
    const picked = survivorPick[g.key] ? g.members.find((m) => m.id === survivorPick[g.key]) : null;
    return picked ?? suggestSurvivor(g);
  };

  const dismiss = async (g: DuplicateGroup) => {
    if (!window.confirm(`Mark these ${g.members.length} records as not duplicates? They will not be paired again.`)) return;
    setBusyKey(g.key);
    const res: any = await dismissLeadDuplicates(g.members.map((m) => m.id));
    setBusyKey(null);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    toast.add("Noted - they will not be paired again.", "var(--theme-ui-colors-success)");
    refetch();
  };

  const runMerge = async () => {
    if (!merge) return;
    setBusyKey(merge.group.key);
    const res: any = await mergeLeadDuplicates(merge.survivor.id, merge.others.map((m) => m.id));
    setBusyKey(null);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    toast.add(`Merged ${res.merged.length} lead${res.merged.length === 1 ? "" : "s"} into ${merge.survivor.name}.`, "var(--theme-ui-colors-success)");
    setMerge(null);
    refetch();
  };

  const open = (m: DuplicateMember) => navigate(m.kind === "client" ? `/clients/${m.id}` : `/leads/${m.id}`);

  return (
    <>
      <Header />
      <Page
        title="Duplicate leads"
        subtitle="Records the identity check pairs by email, company domain, website or name. Merge them, or mark them as not duplicates."
        backAction={<Link variant="back" href="/leads"><BackArrowIcon />Back to Leads</Link>}
        titleAction={
          <BasicButton scheme="secondaryOutline" type="button" keepText onClick={refetch} disabled={loading}>
            {loading ? "Refreshing\u2026" : "Refresh"}
          </BasicButton>
        }
      >
        {error && (
          <Box sx={{ marginBottom: "16px", padding: "10px 14px", background: "#FBEAEA", color: "#A60000", borderRadius: "8px", fontFamily: "Poppins", fontSize: "13px" }}>{error}</Box>
        )}

        <P fontFamily="Poppins" fontSize={"13px"} color={themevals.colors.dkgrey} marginTop={0} marginBottom={"16px"}>
          {loading
            ? "Checking\u2026"
            : groups.length === 0
              ? "No possible duplicates. Every lead looks distinct from every other lead and client."
              : `${groups.length} group${groups.length === 1 ? "" : "s"}. A client in a group is always the one kept: the lead is merged into it and the client's own details are never changed.`}
        </P>

        {ordered.map((g) => {
          const survivor = survivorOf(g);
          const others = g.members.filter((m) => m.id !== survivor.id);
          const hasClient = g.members.some((m) => m.kind === "client");
          const c = CONFIDENCE[g.confidence];
          const focused = !!focus && g.members.some((m) => m.id === focus);
          return (
            <Box
              key={g.key}
              sx={{
                border: `1px solid ${focused ? "#8A5A00" : "#E4E7EB"}`,
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "16px",
                background: "#fff",
                fontFamily: "Poppins",
              }}
            >
              <Flex sx={{ justifyContent: "space-between", alignItems: "flex-start", gap: "12px", marginBottom: "8px", flexWrap: "wrap" }}>
                <Box>
                  <Box sx={{ fontSize: "13px", fontWeight: 600, color: "#111" }}>
                    {g.members.length} records matching{hasClient ? " \u2014 one is a client" : ""}
                  </Box>
                  <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "2px" }}>{g.reasons.join(" \u00b7 ")}</Box>
                </Box>
                <Flex sx={{ gap: "8px", alignItems: "center" }}>
                  <Pill bg={c.bg} fg={c.fg}>{c.label}</Pill>
                  <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => dismiss(g)} disabled={busyKey === g.key}>Not duplicates</BasicButton>
                  <BasicButton type="button" keepText onClick={() => setMerge({ group: g, survivor, others })} disabled={busyKey === g.key || others.length === 0}>
                    Merge {others.length} into {survivor.name}
                  </BasicButton>
                </Flex>
              </Flex>

              {g.members.map((m) => {
                const isSurvivor = m.id === survivor.id;
                return (
                  <Flex key={m.id} sx={{ alignItems: "center", gap: "12px", padding: "10px 0", borderTop: "1px solid #F3F4F6" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ fontSize: "13px", fontWeight: 500, color: "#111", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <span style={{ cursor: "pointer" }} onClick={() => open(m)}>{m.name}</span>
                        <Pill bg={m.kind === "client" ? themevals.colors.black : "#F1EFE8"} fg={m.kind === "client" ? "#fff" : "#5f5e5a"}>{m.kind === "client" ? "Client" : "Lead"}</Pill>
                        {m.kind === "lead" && m.leadStatus && (
                          <Pill bg="#F1EFE8" fg="#5f5e5a">{LEAD_STATUS_LABELS[m.leadStatus as keyof typeof LEAD_STATUS_LABELS] ?? m.leadStatus}</Pill>
                        )}
                        {isSurvivor && <Pill bg="#DCFCE7" fg="#166534">{hasClient ? "Kept (client)" : "Kept"}</Pill>}
                      </Box>
                      <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "2px" }}>
                        {[m.contactName, m.contactEmail, m.phone, m.website].filter(Boolean).join(" \u00b7 ") || "no contact details"}
                      </Box>
                      <Box sx={{ fontSize: "11px", color: "#78716C", marginTop: "4px" }}>
                        {[
                          `${m.rfqCount} RFQ${m.rfqCount === 1 ? "" : "s"}`,
                          `${m.quoteCount} quote${m.quoteCount === 1 ? "" : "s"}`,
                          `${m.proposalCount} proposal${m.proposalCount === 1 ? "" : "s"}`,
                          `${m.siteCount} site${m.siteCount === 1 ? "" : "s"}`,
                          `${m.noteCount} note${m.noteCount === 1 ? "" : "s"}`,
                          m.ownerName ? `owner ${m.ownerName}` : null,
                          m.nextActionAt ? `next action ${moment(m.nextActionAt).format("MMM D")}` : null,
                          `created ${moment(m.createdAt).format("MMM D, YYYY")}`,
                        ]
                          .filter(Boolean)
                          .join(" \u00b7 ")}
                      </Box>
                    </Box>
                    <Flex sx={{ gap: "6px", flexShrink: 0 }}>
                      {!isSurvivor && !hasClient && (
                        <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => setSurvivorPick((p) => ({ ...p, [g.key]: m.id }))}>
                          Keep this one
                        </BasicButton>
                      )}
                      <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => open(m)}>Open</BasicButton>
                    </Flex>
                  </Flex>
                );
              })}
            </Box>
          );
        })}

        {merge && (
          <BasicModal
            title={`Merge into ${merge.survivor.name}?`}
            cancel_click={() => setMerge(null)}
            cancel_text="Cancel"
            confirm_text={busyKey === merge.group.key ? "Merging\u2026" : `Merge ${merge.others.length} lead${merge.others.length === 1 ? "" : "s"}`}
            submitting={busyKey === merge.group.key}
            confirm_click={runMerge}
          >
            <P fontFamily="Poppins" fontSize={"13px"} marginTop={0} marginBottom={"8px"} color={themevals.colors.black}>
              {merge.others.map((m) => m.name).join(", ")} will be merged into <b>{merge.survivor.name}</b>{merge.survivor.kind === "client" ? " (a client)" : ""}.
            </P>
            <P fontFamily="Poppins" fontSize={"12px"} marginTop={0} marginBottom={"6px"} color={themevals.colors.dkgrey}>
              Their notes, activity, RFQs, quotes, proposals and sites move across. Quotes and their public links stay exactly as issued - no re-pricing.
            </P>
            <P fontFamily="Poppins" fontSize={"12px"} marginTop={0} marginBottom={0} color={themevals.colors.dkgrey}>
              {merge.survivor.kind === "client"
                ? "Nothing on the client changes; each merged lead is summarised as a note on it."
                : "Blank fields on the kept lead are filled from the others; filled fields are never overwritten. The earliest next action wins."}
              {" "}The merged leads are removed from the list (soft deleted).
            </P>
          </BasicModal>
        )}
      </Page>
    </>
  );
};

export default LeadDuplicates;
